import type {
  CellStatus,
  CellCapacityInfo,
  CapacityMap,
} from '../types/timesheet.types';
import type { UserDayCapacity } from '../api/timesheet.api';

/**
 * Compute the visual status of a cell based on actual hours and capacity.
 */
export function computeCellStatus(
  actual: number,
  capacityHours: number,
  isHoliday: boolean,
  isWeekend: boolean,
  isLeave: boolean,
  availableHours: number,
): CellStatus {
  if (isHoliday && actual === 0) return 'holiday';
  if (isWeekend && actual === 0) return 'weekend';
  if (isLeave && actual === 0) return 'leave';
  if (availableHours === 0 && actual === 0) return 'no_data';
  if (actual === 0) return 'no_data';

  // Working day with logged hours
  if (availableHours === 0 && actual > 0) return 'over'; // Logged on non-working day
  const ratio = actual / availableHours;
  if (ratio > 1.0) return 'over';
  if (ratio >= 0.8) return 'at_capacity';
  return 'normal';
}

/**
 * Build a CapacityMap from the API response.
 */
export function buildCapacityMap(
  capacities: UserDayCapacity[],
): CapacityMap {
  const map: CapacityMap = {};

  for (const cap of capacities) {
    if (!map[cap.userId]) {
      map[cap.userId] = {};
    }

    const status = computeCellStatus(
      0, // actual is unknown at this point, will be recomputed during render
      cap.capacityHours,
      cap.isHoliday,
      cap.isWeekend,
      cap.isLeave,
      cap.availableHours,
    );

    map[cap.userId][cap.date] = {
      capacityHours: cap.capacityHours,
      isHoliday: cap.isHoliday,
      isWeekend: cap.isWeekend,
      isLeave: cap.isLeave,
      availableHours: cap.availableHours,
      status,
    };
  }

  return map;
}

/**
 * Get the cell capacity info for a specific user + date, recomputing status
 * with the actual hours value.
 */
export function getCellCapacity(
  capacityMap: CapacityMap,
  userId: string,
  date: string,
  actualHours: number,
): CellCapacityInfo | null {
  const userCaps = capacityMap[userId];
  if (!userCaps) return null;
  const dayInfo = userCaps[date];
  if (!dayInfo) return null;

  return {
    ...dayInfo,
    status: computeCellStatus(
      actualHours,
      dayInfo.capacityHours,
      dayInfo.isHoliday,
      dayInfo.isWeekend,
      dayInfo.isLeave,
      dayInfo.availableHours,
    ),
  };
}

/**
 * Format hours as a decimal number for reports/timesheets.
 * Examples: 0 -> "-", 8 -> "8", 4.5 -> "4.5", 0.0167 -> "0.02"
 * Always shows up to 2 decimal places, strips unnecessary trailing zeros.
 */
export function formatHours(hours: number): string {
  if (hours === 0) return '-';
  const rounded = Math.round(hours * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/0$/, '');
}

/**
 * Format hours using hours+minutes notation for time tracking UI.
 * Examples: 0 -> "-", 8 -> "8h", 4.5 -> "4h 30m", 0.0167 -> "1m"
 */
export function formatHoursMinutes(hours: number): string {
  if (hours === 0) return '-';
  const abs = Math.abs(hours);
  const h = Math.floor(abs);
  const m = Math.round((abs - h) * 60);
  const sign = hours < 0 ? '-' : '';
  if (m === 0) return `${sign}${h}h`;
  if (h === 0) return `${sign}${m}m`;
  return `${sign}${h}h ${m}m`;
}

/**
 * Split decimal hours into whole hours + minutes.
 * Examples: 8.5 -> { h: '8', m: '30' }, 0.25 -> { h: '0', m: '15' }
 */
export function splitHoursMinutes(decimal: number): { h: string; m: string } {
  const h = Math.floor(Math.abs(decimal));
  const m = Math.round((Math.abs(decimal) - h) * 60);
  return { h: String(h), m: String(m) };
}

/**
 * Combine hours + minutes string inputs into decimal hours.
 * Examples: ('8', '30') -> 8.5, ('0', '15') -> 0.25, ('', '45') -> 0.75
 */
export function combineHoursMinutes(hoursInput: string, minutesInput: string): number {
  return (parseInt(hoursInput, 10) || 0) + (parseInt(minutesInput, 10) || 0) / 60;
}

/**
 * Format progress percentage (e.g., 32.7 -> "32.7%")
 */
export function formatProgress(progress: number | null): string {
  if (progress === null) return '-';
  return progress.toFixed(2);
}
